import { useState } from 'react'

import { Box, Chip, Menu, MenuItem, Divider } from '@mui/material'
import { IconTemplate } from '@tabler/icons-react'
import type { Page } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery, useMutation } from '@redwoodjs/web'
import { toast } from '@redwoodjs/web/toast'

import ObjectChip from 'src/components/Chips/ObjectChip/ObjectChip'
import { CREATE_PAGE } from 'src/components/Pages/mutations'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes, TemplateTypes } from 'src/lib/objects'

import { GET_WORKSPACE_TEMPLATES } from '../queries'

const templateTypes = [
  { value: 'EMAIL', label: 'Email Template', icon: <IconTemplate size={16} /> },
  {
    value: 'INTERNAL_PAGE',
    label: 'Page Template',
    icon: <IconTemplate size={16} />,
  },
  {
    value: 'KNOWLEDGE',
    label: 'Knowledge Template',
    icon: <IconTemplate size={16} />,
  },
  { value: 'SLACK', label: 'Slack Template', icon: <IconTemplate size={16} /> },
]

const TemplatePicker = ({
  workspaceId,
  setTemplate,
  hiddenTemplateIds = [],
}: {
  workspaceId: string
  setTemplate: (template: Partial<Page> | null) => void
  hiddenTemplateIds?: string[]
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const { data: templates, refetch } = useQuery(GET_WORKSPACE_TEMPLATES, {
    variables: { workspaceId },
    skip: !workspaceId,
  })

  const [createPage] = useMutation(CREATE_PAGE, {
    onCompleted: () => {
      refetch()
    },
  })

  const filteredTemplates = templates?.workspaceTemplates?.filter(
    (template: Page) => !hiddenTemplateIds.includes(template.id)
  )

  const handleCreateBlankTemplate = async (templateType: string) => {
    const templateTypeLabels = {
      EMAIL: 'Email',
      INTERNAL_PAGE: 'Page',
      KNOWLEDGE: 'Knowledge',
      SLACK: 'Slack',
    }

    const title = `Untitled ${templateTypeLabels[templateType]} Template`

    try {
      const { data } = await createPage({
        variables: {
          input: {
            title,
            workspaceId,
            templateType,
          },
        },
      })

      logger.info('Created new template', { templateType, data })

      if (data?.createPage?.id) {
        navigate(routes.pageDetail({ id: data.createPage.id }))
      }
    } catch (error) {
      toast.error('Failed to create template: ' + error.message)
    }
  }

  return (
    <>
      <Box sx={{ height: 'auto' }}>
        <Chip
          clickable={true}
          variant="outlined"
          size="small"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            p: 0,
          }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          label={
            <ObjectChip
              workspaceId={workspaceId}
              crmObject={{
                objectType: NativeObjectTypes.Page,
                objectId: 'CHOOSE_TEMPLATE',
                properties: {
                  templateType: TemplateTypes.BASE,
                  label: 'Choose template',
                },
              }}
            />
          }
        />
      </Box>
      <Menu
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredTemplates?.slice(0, 3)?.map((menuTemplate: Page) => (
          <MenuItem
            onClick={() => setTemplate(menuTemplate)}
            key={menuTemplate.id}
          >
            <ObjectChip
              workspaceId={workspaceId}
              crmObject={{
                objectType: NativeObjectTypes.Page,
                objectId: menuTemplate.id,
                properties: menuTemplate,
              }}
            />
          </MenuItem>
        ))}

        <Divider />

        {templateTypes.map((template) => (
          <MenuItem
            key={template.value}
            onClick={() => handleCreateBlankTemplate(template.value)}
          >
            {template.icon}
            <span style={{ marginLeft: 8 }}>{template.label}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default TemplatePicker
